import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useSetupReminder } from "./use-setup-reminder";

/**
 * Invasive, dashboard-level reminder for important setup tasks the account
 * owner hasn't finished — completing the business profile or connecting
 * integrations. Renders a centered modal (à la the "Connect your email"
 * takeover) rather than a passive dashboard card. Mounted once in AppLayout so
 * it can appear on any authenticated route.
 */
export function SetupReminderModal() {
  const navigate = useNavigate();
  const { active, dismissForever, snooze } = useSetupReminder();

  if (!active) return null;

  const { view } = active;
  const Icon = view.icon;

  // Closing via the X, Escape or overlay click counts as "maybe later" so the
  // reminder doesn't re-pop on the next navigation within this session.
  const handleOpenChange = (open: boolean) => {
    if (!open) snooze(active.id);
  };

  const handleCta = () => {
    // Treat acting on the CTA as a session snooze — they're on their way to
    // fix it; no need to nag again this session.
    snooze(active.id);
    navigate(view.cta.to);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <DialogTitle className="text-left">{view.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-1 text-left">
            {view.description}
          </DialogDescription>
        </DialogHeader>

        {view.progress ? (
          <p className="text-sm font-medium text-muted-foreground">
            {view.progress.done} av {view.progress.total} steg klara
          </p>
        ) : null}

        <div className="space-y-2">
          {view.benefits.map((benefit, i) => {
            const BenefitIcon = benefit.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3"
              >
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground">
                  <BenefitIcon className="h-4 w-4" />
                </div>
                <p className="text-sm text-foreground">{benefit.text}</p>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleCta}
          className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {view.cta.label}
          <ArrowRight className="h-4 w-4" />
        </button>

        <div className="flex items-center justify-end gap-4 text-sm">
          <button
            type="button"
            onClick={() => dismissForever(active.id)}
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            Visa inte igen
          </button>
          <button
            type="button"
            onClick={() => snooze(active.id)}
            className="font-medium text-foreground transition-colors hover:text-foreground/80"
          >
            Kanske senare
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
